import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Snackbar,
  IconButton,
  Stack,
  LinearProgress,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import { db } from "../../../config/firebase";
import {
  collection,
  getDocs,
  addDoc,
  setDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import ModalDialogTitle from "../../common/ModalDialogTitle";
import ModalActions from "../../common/ModalActions";
import ConfirmDeleteDialog from "../../common/ConfirmDeleteDialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileImport,
  faFileDownload,
} from "@fortawesome/free-solid-svg-icons";
import { faFileExcel } from "@fortawesome/pro-solid-svg-icons";
import { downloadExcelTemplate } from "../../../utils/exportTemplate";

const initialForm = { statusNote: "" };

const PTPStatusNote = () => {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState({ statusNote: "" });
  const [filteredData, setFilteredData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [importStatus, setImportStatus] = useState({
    open: false,
    added: 0,
    updated: 0,
    total: 0,
    loading: false,
  });
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const COLLECTION_NAME = "PTPStatusNote";

  useEffect(() => {
    const fetchData = async () => {
      const snapshot = await getDocs(collection(db, COLLECTION_NAME));
      let rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sort by statusNote alphabetically
      rows = rows.sort((a, b) =>
        (a.statusNote || "").localeCompare(b.statusNote || ""),
      );
      setData(rows);
    };
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = data.filter(
      (row) =>
        !filter.statusNote ||
        (row.statusNote || "")
          .toLowerCase()
          .includes(filter.statusNote.toLowerCase()),
    );
    // Sort filtered results by statusNote alphabetically
    filtered = filtered.sort((a, b) =>
      (a.statusNote || "").localeCompare(b.statusNote || ""),
    );
    setFilteredData(filtered);
  }, [data, filter]);

  const handleOpenAdd = () => {
    setEditIndex(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const handleOpenEdit = (params) => {
    const idx = data.findIndex((d) => d.statusNote === params.row.statusNote);
    setEditIndex(idx);
    setForm(params.row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Only ask for confirmation when editing an existing record — a fresh
  // Add form has nothing at risk yet. The X (and backdrop/Escape, which
  // share this same handler) go through the same check.
  const handleCloseAttempt = () => {
    if (editIndex !== null) {
      setConfirmCloseOpen(true);
    } else {
      handleCloseModal();
    }
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    handleCloseModal();
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (!form.statusNote) {
      setSnackbar({
        open: true,
        message: "Status Note is required.",
        severity: "error",
      });
      return;
    }
    try {
      if (editIndex !== null) {
        const docId = data[editIndex].id;
        await setDoc(doc(db, COLLECTION_NAME, docId), form);
        const newData = [...data];
        newData[editIndex] = { ...form, id: docId };
        setData(newData);
        setSnackbar({
          open: true,
          message: "Status Note updated",
          severity: "success",
        });
      } else {
        const docRef = await addDoc(collection(db, COLLECTION_NAME), form);
        setData([...data, { ...form, id: docRef.id }]);
        setSnackbar({
          open: true,
          message: "Status Note added",
          severity: "success",
        });
      }
      setModalOpen(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error saving to Firestore",
        severity: "error",
      });
    }
  };

  const handleDeleteStatusNote = async () => {
    if (editIndex !== null) {
      const docId = data[editIndex].id;
      await deleteDoc(doc(db, COLLECTION_NAME, docId));
      const newData = data.filter((_, idx) => idx !== editIndex);
      setData(newData);
      setModalOpen(false);
      setSnackbar({
        open: true,
        message: "Status Note deleted",
        severity: "success",
      });
    }
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportStatus({
      open: true,
      added: 0,
      updated: 0,
      total: 0,
      loading: true,
    });
    const reader = new FileReader();

    const processExcelRow = async (row, merged, counts) => {
      const { added, updated } = counts;
      const idx = merged.findIndex((d) => d.statusNote === row.statusNote);
      if (idx > -1) {
        const docId = merged[idx].id;
        await setDoc(doc(db, COLLECTION_NAME, docId), {
          statusNote: row.statusNote,
        });
        merged[idx] = { ...merged[idx], statusNote: row.statusNote };
        counts.updated = updated + 1;
      } else {
        const docRef = await addDoc(collection(db, COLLECTION_NAME), {
          statusNote: row.statusNote,
        });
        merged.push({ statusNote: row.statusNote, id: docRef.id });
        counts.added = added + 1;
      }
    };

    reader.onload = async (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const jsonData = XLSX.utils.sheet_to_json(ws);

      const merged = [...data];
      const counts = { added: 0, updated: 0 };

      for (const row of jsonData) {
        if (row.statusNote) {
          await processExcelRow(row, merged, counts);
          setImportStatus((s) => ({
            ...s,
            total: jsonData.length,
            added: counts.added,
            updated: counts.updated,
          }));
        }
      }

      setData(merged);
      setSnackbar({
        open: true,
        message: "Excel data imported",
        severity: "success",
      });
      setImportStatus((s) => ({ ...s, loading: false }));
    };

    reader.readAsBinaryString(file);
  };

  const handleDeleteMultipleConfirmed = async () => {
    for (const id of rowSelectionModel) {
      await deleteDoc(doc(db, COLLECTION_NAME, id));
    }
    setData(data.filter((row) => !rowSelectionModel.includes(row.id)));
    setRowSelectionModel([]);
    setConfirmDeleteOpen(false);
    setSnackbar({
      open: true,
      message: "Selected status notes deleted",
      severity: "success",
    });
  };

  const handleOpenConfirmDelete = () => {
    if (!rowSelectionModel || rowSelectionModel.length === 0) return;
    setConfirmDeleteOpen(true);
  };

  const handleDownloadTemplate = () => {
    const headers = ["statusNote"];
    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
    XLSX.writeFile(workbook, "ptp-status-note-template.xlsx");
  };

  const handleExportTemplate = () => {
    downloadExcelTemplate(
      Object.keys(initialForm),
      `${COLLECTION_NAME}-template`,
    );
  };

  const rowsForGrid = filteredData.map((row) => ({
    ...row,
    id: String(row.id),
  }));

  const columns = [
    { field: "statusNote", headerName: "Status Note", flex: 1, sortable: true },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          color="primary"
          onClick={() => handleOpenEdit(params)}
          size="small"
        >
          <EditIcon />
        </IconButton>
      ),
      width: 90,
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 1 }}>
        PTP Status Note Management
      </Typography>
      <TechPrepPortalMgtBreadCrumbs pageName="PTP Status Note" />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        mb={2}
        alignItems="center"
        sx={{ mt: 3, mb: 3 }}
      >
        <TextField
          label="Filter Status Note"
          name="statusNote"
          value={filter.statusNote}
          onChange={(e) => setFilter({ ...filter, statusNote: e.target.value })}
          size="small"
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
        >
          Add
        </Button>
        <IconButton component="label" size="medium">
          <FontAwesomeIcon icon={faFileImport} color="#2e7d32" />
          <input
            type="file"
            accept=".xlsx, .xls"
            hidden
            onChange={handleExcelUpload}
          />
        </IconButton>
        <IconButton onClick={handleDownloadTemplate} size="medium">
          <FontAwesomeIcon icon={faFileDownload} color="#1976d2" />
        </IconButton>
        <Button
          variant="contained"
          color="success"
          startIcon={<FontAwesomeIcon icon={faFileExcel} />}
          sx={{ color: "white" }}
          onClick={handleExportTemplate}
        >
          Export Template
        </Button>
        {rowSelectionModel && rowSelectionModel.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleOpenConfirmDelete}
            sx={{ ml: 1 }}
          >
            Delete Selected
          </Button>
        )}
        <ClearCollectionButton collectionName="PTPStatusNote" />
      </Stack>
      <Box
        sx={{
          height: 500,
          width: "100%",
          background: "#fff",
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <DataGrid
          rows={rowsForGrid}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          disableSelectionOnClick
          onRowDoubleClick={handleOpenEdit}
          checkboxSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newSelectionModel) => {
            setRowSelectionModel(newSelectionModel);
          }}
        />
      </Box>
      <Dialog
        open={modalOpen}
        onClose={handleCloseAttempt}
        maxWidth="xs"
        fullWidth
      >
        <ModalDialogTitle onClose={handleCloseAttempt}>
          {editIndex !== null ? "Edit Status Note" : "Add Status Note"}
        </ModalDialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Status Note"
              name="statusNote"
              value={form.statusNote}
              onChange={handleFormChange}
              required
              fullWidth
            />
          </Stack>
        </DialogContent>
        <ModalActions
          onSave={handleSave}
          onDelete={editIndex !== null ? handleDeleteStatusNote : undefined}
        />
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteMultipleConfirmed}
        title="Delete Selected Status Notes"
        message={`Are you sure you want to delete ${
          rowSelectionModel.length
        } selected status note${
          rowSelectionModel.length === 1 ? "" : "s"
        }? This action cannot be undone.`}
      />
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
      {importStatus.open && (
        <Box sx={{ mt: 2 }}>
          {importStatus.loading && <LinearProgress />}
          <Alert severity="info" sx={{ mt: 1 }}>
            Importing Excel:{" "}
            {importStatus.loading ? "Processing..." : "Complete."} <br />
            Added: {importStatus.added} &nbsp; Updated: {importStatus.updated}{" "}
            &nbsp; Total: {importStatus.total}
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default PTPStatusNote;
